import React, { useEffect, useState } from 'react';
import TuMesaYaForm from '../components/Form/TuMesaYaForm';
import { useTracking } from '../hooks/useTracking';
import { LOGIN_URL, REGISTER_URL } from '../config/appLinks';
import { submitLeadToWebhook } from '../utils/api';
import { FormData } from '../utils/validation';
import logo from '../shared/assets/images/logo-tumesaya.svg';
import busyServiceImage from '../shared/assets/images/restaurant-service-busy-v2.webp';
import appImage from '../shared/assets/images/img-appmovil.webp';
import testimonialImage from '../shared/assets/images/senora-opinion2.avif';
import facebookIcon from '../shared/assets/icons/facebook-icon.svg';
import linkedinIcon from '../shared/assets/icons/linkedin-icon.svg';
import './landing.css';

type SubmitState = 'idle' | 'success' | 'error';
type Channel = 'phone' | 'whatsapp';
type ReminderState = 'pending' | 'confirmed' | 'cancelled';

const PhoneDemo: React.FC = () => (
  <div className="channel-screen channel-screen--phone" key="phone">
    <div className="call-state">
      <span className="call-state__icon" aria-hidden="true">
        ☎
      </span>
      <div>
        <span>Llamada en curso</span>
        <strong>00:42</strong>
      </div>
      <div className="sound-wave" aria-hidden="true">
        {[6, 16, 10, 22, 14, 28, 18, 12, 24, 8, 18, 12].map((height, index) => (
          <i key={index} style={{ height }} />
        ))}
      </div>
    </div>
    <div className="transcript">
      <p>
        <span>Cliente</span>
        Quería una mesa para cuatro este sábado a las nueve.
      </p>
      <p>
        <span>TuMesaYa</span>
        Perfecto, tengo disponibilidad. ¿Confirmamos la reserva?
      </p>
    </div>
    <div className="screen-result">
      <span className="status-check">✓</span>
      <div>
        <span>Reserva confirmada</span>
        <strong>Sábado · 21:00 · 4 personas</strong>
      </div>
    </div>
  </div>
);

const WhatsAppDemo: React.FC = () => (
  <div className="channel-screen channel-screen--whatsapp" key="whatsapp">
    <div className="wa-header">
      <span className="wa-mark">WA</span>
      <div>
        <strong>TuMesaYa</strong>
        <span>ejemplo · canal no disponible</span>
      </div>
    </div>
    <div className="wa-chat">
      <p className="wa-bubble wa-bubble--customer">
        Hola, ¿tenéis mesa para dos esta noche?
        <small>18:21</small>
      </p>
      <p className="wa-bubble wa-bubble--bot">
        Sí, a las 21:30. ¿Quieres que la reserve a tu nombre?
        <small>18:21 ✓✓</small>
      </p>
      <p className="wa-bubble wa-bubble--customer">
        Sí, perfecto.
        <small>18:22</small>
      </p>
    </div>
    <div className="screen-result screen-result--wa">
      <span className="status-check">✓</span>
      <div>
        <span>Reserva confirmada por WhatsApp</span>
        <strong>Hoy · 21:30 · 2 personas</strong>
      </div>
    </div>
  </div>
);

const LandingPage: React.FC = () => {
  const [activeChannel, setActiveChannel] = useState<Channel>('phone');
  const [reminderState, setReminderState] = useState<ReminderState>('pending');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [hideStickyCta, setHideStickyCta] = useState(false);
  const { trackCTAClick, trackPageView } = useTracking();

  useEffect(() => {
    trackPageView('landing-principal', 'organic');

    const revealObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.14 }
    );

    const revealElements = document.querySelectorAll('[data-reveal]');
    revealElements.forEach(element => revealObserver.observe(element));

    const demoSection = document.getElementById('demo');
    const stickyObserver = new IntersectionObserver(
      entries => setHideStickyCta(entries.some(entry => entry.isIntersecting)),
      { threshold: 0.08 }
    );

    if (demoSection) stickyObserver.observe(demoSection);

    return () => {
      revealObserver.disconnect();
      stickyObserver.disconnect();
    };
  }, [trackPageView]);

  const handleDemoClick = (source: string) => {
    trackCTAClick('solicitar-demo', source);
  };

  const handleFormSubmit = async (formData: FormData) => {
    setIsSubmitting(true);
    setSubmitState('idle');

    const response = await submitLeadToWebhook({
      ...formData,
      source: 'demo-request',
      timestamp: new Date().toISOString(),
    });

    setIsSubmitting(false);
    setSubmitState(response.success ? 'success' : 'error');
    return response.success;
  };

  return (
    <div className="site-shell site-shell--v2">
      <a className="skip-link" href="#contenido">
        Saltar al contenido
      </a>

      <a className="campaign-access" href="/prueba">
        <span>Acceso campaña</span>
        <strong>Activa tu prueba gratuita de 30 min</strong>
        <span aria-hidden="true">↗</span>
      </a>

      <header className="main-header main-header--yellow">
        <a className="brand" href="/" aria-label="TuMesaYa, inicio">
          <img src={logo} alt="TuMesaYa" />
        </a>
        <nav className="main-nav" aria-label="Navegación principal">
          <a href="#canales">
            Teléfono + WhatsApp
            <span className="tab-soon tab-soon--ink">Próximamente</span>
          </a>
          <a href="#servicio">Durante el servicio</a>
          <a href="#no-shows">Menos no-shows</a>
        </nav>
        <div className="header-actions">
          <a
            className="header-login"
            href={LOGIN_URL}
            onClick={() => handleDemoClick('header-login')}
          >
            Acceder
          </a>
          <a
            className="button button--dark header-cta"
            href={REGISTER_URL}
            onClick={() => handleDemoClick('header-alta')}
          >
            Crear cuenta
          </a>
        </div>
      </header>

      <main id="contenido">
        <section className="hero-v2" aria-labelledby="hero-title">
          <div className="hero-v2__copy">
            <div className="live-label">
              <span /> Central de reservas activa · 24/7
            </div>
            <h1 id="hero-title">
              Las reservas entran.
              <br />
              <span>Tu equipo sigue a lo suyo.</span>
            </h1>
            <p>
              TuMesaYa atiende las llamadas, confirma mesas, envía recordatorios
              y gestiona cancelaciones, incluso cuando el restaurante está
              cerrado. El canal de WhatsApp llegará próximamente.
            </p>
            <div className="hero-v2__actions">
              <a
                className="button button--yellow button--wide"
                href={REGISTER_URL}
                onClick={() => handleDemoClick('hero-alta')}
              >
                Crear mi cuenta
              </a>
              <a
                className="inline-link"
                href="#demo"
                onClick={() => handleDemoClick('hero-demo')}
              >
                Prefiero ver una demo <span aria-hidden="true">↓</span>
              </a>
            </div>
            <ul className="hero-v2__proof" aria-label="Funciones principales">
              <li>
                <strong>1 canal activo</strong>
                <span>Teléfono. WhatsApp, próximamente</span>
              </li>
              <li>
                <strong>24/7</strong>
                <span>También fuera de horario</span>
              </li>
              <li>
                <strong>1 agenda</strong>
                <span>Todo queda sincronizado</span>
              </li>
            </ul>
          </div>

          <div
            className="hero-console"
            aria-label="Actividad de reservas en directo"
          >
            <div className="hero-console__header">
              <div>
                <span className="console-light" />
                <strong>Actividad en directo</strong>
              </div>
              <span>Ahora</span>
            </div>
            <div className="activity-feed">
              <article className="activity-row activity-row--phone">
                <span className="activity-icon">TEL</span>
                <div>
                  <strong>Llamada atendida</strong>
                  <span>4 personas · Sáb 21:00</span>
                </div>
                <b>Confirmada</b>
              </article>
              <div className="coming-soon coming-soon--row">
                <p className="coming-soon__band coming-soon__band--sm">
                  Próximamente
                </p>
                <div className="coming-soon__preview">
                  <article className="activity-row activity-row--wa">
                    <span className="activity-icon">WA</span>
                    <div>
                      <strong>Reserva por WhatsApp</strong>
                      <span>Ejemplo · aún no disponible</span>
                    </div>
                    <b>Ejemplo</b>
                  </article>
                </div>
              </div>
              <article className="activity-row activity-row--reminder">
                <span className="activity-icon">24H</span>
                <div>
                  <strong>Recordatorio enviado</strong>
                  <span>Mesa de Lucía · mañana</span>
                </div>
                <b>Entregado</b>
              </article>
              <article className="activity-row activity-row--cancel">
                <span className="activity-icon">↺</span>
                <div>
                  <strong>Cancelación gestionada</strong>
                  <span>Mesa liberada automáticamente</span>
                </div>
                <b>Disponible</b>
              </article>
            </div>
            <div className="console-summary">
              <span>Hoy</span>
              <strong>18 reservas gestionadas</strong>
              <div className="summary-bars" aria-hidden="true">
                {[35, 52, 44, 72, 58, 88, 76, 94, 68, 82].map(
                  (height, index) => (
                    <i key={index} style={{ height: `${height}%` }} />
                  )
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="channel-section" id="canales">
          <div className="section-intro" data-reveal>
            <span className="section-index">
              01 · CANALES
              <span className="tab-soon">WhatsApp, próximamente</span>
            </span>
            <h2>
              Una reserva empieza con una llamada. Pronto, también con un
              mensaje.
            </h2>
            <p>
              El teléfono ya lo atiende el asistente 24/7. WhatsApp llegará
              próximamente: cuando esté, serán dos entradas a la misma agenda.
            </p>
          </div>

          <div className="channel-stage" data-reveal>
            <div
              className="channel-tabs"
              role="tablist"
              aria-label="Canal de reserva"
            >
              <button
                type="button"
                role="tab"
                aria-selected={activeChannel === 'phone'}
                className={activeChannel === 'phone' ? 'is-active' : ''}
                onClick={() => setActiveChannel('phone')}
              >
                <span className="tab-icon">TEL</span>
                Llamada telefónica
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeChannel === 'whatsapp'}
                className={activeChannel === 'whatsapp' ? 'is-active' : ''}
                onClick={() => setActiveChannel('whatsapp')}
              >
                <span className="tab-icon tab-icon--wa">WA</span>
                WhatsApp
                <span className="tab-soon">Próximamente</span>
              </button>
            </div>
            <div className="channel-panel" role="tabpanel">
              {activeChannel === 'phone' ? (
                <PhoneDemo />
              ) : (
                <div className="coming-soon">
                  <p className="coming-soon__band">Próximamente</p>
                  <div className="coming-soon__preview">
                    <WhatsAppDemo />
                  </div>
                </div>
              )}
            </div>
            <aside className="unified-agenda">
              <span>Una única agenda</span>
              <strong>Sin reservas duplicadas. Sin copiar datos.</strong>
              <div className="agenda-flow" aria-hidden="true">
                <i>TEL</i>
                <span />
                <b>RESERVAS</b>
                <span />
                <i className="agenda-flow__wa is-soon">WA</i>
              </div>
              <p className="agenda-flow__note">
                <span className="tab-soon tab-soon--ink">Próximamente</span>
                La entrada por WhatsApp todavía no está disponible. Hoy funciona
                el canal de teléfono.
              </p>
            </aside>
          </div>
        </section>

        <section className="service-section" id="servicio">
          <div className="service-photo" data-reveal>
            <img
              src={busyServiceImage}
              alt="Restaurante lleno con un camarero tomando una comanda y otro cliente reclamando su atención"
              loading="lazy"
            />
            <div className="service-photo__signal">
              <span className="ring-dot" />
              <div>
                <strong>Nueva llamada</strong>
                <span>TuMesaYa la está atendiendo</span>
              </div>
            </div>
          </div>
          <div className="service-copy" data-reveal>
            <span className="section-index">02 · EN PLENO SERVICIO</span>
            <h2>
              Que suene el teléfono no significa que alguien tenga que dejar la
              sala.
            </h2>
            <p>
              En la hora punta, cada interrupción pesa. TuMesaYa responde,
              comprueba disponibilidad y confirma la mesa mientras tu equipo
              sigue atendiendo a quien ya está en el restaurante.
            </p>
            <div className="impact-list">
              <div>
                <span>Antes</span>
                <strong>Cortar el servicio para coger el teléfono</strong>
              </div>
              <div>
                <span>Con TuMesaYa</span>
                <strong>La reserva entra sin interrumpir a nadie</strong>
              </div>
            </div>
          </div>
        </section>

        <section className="always-on-section" data-reveal>
          <div className="clock-visual" aria-hidden="true">
            <div className="clock-face">
              <span className="clock-hand clock-hand--hour" />
              <span className="clock-hand clock-hand--minute" />
              <b>24/7</b>
            </div>
            <div className="clock-orbit">
              <i />
            </div>
          </div>
          <div>
            <span className="section-index section-index--yellow">
              03 · CUANDO CIERRAS
            </span>
            <h2>Tu restaurante descansa. La entrada de reservas no.</h2>
            <p>
              De noche, entre servicios o durante el día de descanso, el
              teléfono sigue disponible para informar, reservar y confirmar.
              WhatsApp se sumará próximamente.
            </p>
            <ul className="availability-list">
              <li>
                <span>22:47</span>
                <strong>Reserva telefónica confirmada</strong>
              </li>
              <li className="availability-list__soon">
                <span>00:18</span>
                <strong>
                  Consulta de horario por WhatsApp resuelta
                  <span className="tab-soon">Próximamente</span>
                </strong>
              </li>
              <li>
                <span>09:05</span>
                <strong>Agenda lista antes de abrir</strong>
              </li>
            </ul>
          </div>
        </section>

        <section className="no-show-section" id="no-shows">
          <div className="no-show-copy" data-reveal>
            <span className="section-index">04 · DESPUÉS DE RESERVAR</span>
            <h2>WhatsApp también ayudará a que la mesa no se quede vacía.</h2>
            <p className="section-soon">
              <span className="tab-soon tab-soon--ink">Próximamente</span>
              Los recordatorios por WhatsApp aún no están disponibles. Esto es
              un ejemplo de cómo funcionarán cuando lancemos el canal.
            </p>
            <p>
              TuMesaYa enviará recordatorios con la información de la reserva.
              El cliente podrá confirmar o cancelar desde el propio mensaje y la
              mesa volverá a quedar disponible.
            </p>
            <ol className="reminder-steps">
              <li>
                <span>1</span>
                <strong>Enviará el recordatorio</strong>
              </li>
              <li>
                <span>2</span>
                <strong>El cliente confirmará o cancelará</strong>
              </li>
              <li>
                <span>3</span>
                <strong>La agenda se actualizará sola</strong>
              </li>
            </ol>
          </div>

          <div
            className="coming-soon coming-soon--phone"
            data-reveal
            role="group"
            aria-label="Ejemplo del recordatorio por WhatsApp. Función próximamente, todavía no disponible."
          >
            <p className="coming-soon__band coming-soon__band--ink coming-soon__band--top">
              Próximamente
            </p>
            <div className="coming-soon__preview reminder-phone">
              <div className="reminder-phone__speaker" />
              <div className="reminder-phone__header">
                <span className="wa-mark">WA</span>
                <div>
                  <strong>TuMesaYa</strong>
                  <span>Recordatorio de reserva · ejemplo</span>
                </div>
              </div>
              <div className="reminder-message">
                <p>
                  Hola, Marta 👋 Te recordamos tu reserva para mañana a las
                  21:00, 4 personas.
                </p>
                {reminderState === 'pending' && (
                  <div className="message-actions">
                    <button
                      type="button"
                      onClick={() => setReminderState('confirmed')}
                    >
                      ✓ Confirmar
                    </button>
                    <button
                      type="button"
                      onClick={() => setReminderState('cancelled')}
                    >
                      Cancelar reserva
                    </button>
                  </div>
                )}
                <small>18:00 ✓✓</small>
              </div>
              <div aria-live="polite">
                {reminderState === 'cancelled' && (
                  <div className="cancel-notice">
                    <span>↺</span>
                    <div>
                      <strong>Cancelación recibida</strong>
                      <small>Mesa disponible de nuevo</small>
                    </div>
                  </div>
                )}
                {reminderState === 'confirmed' && (
                  <div className="cancel-notice cancel-notice--confirmed">
                    <span>✓</span>
                    <div>
                      <strong>Reserva confirmada</strong>
                      <small>La agenda ya está actualizada</small>
                    </div>
                  </div>
                )}
                {reminderState !== 'pending' && (
                  <button
                    className="reminder-reset"
                    type="button"
                    onClick={() => setReminderState('pending')}
                  >
                    Repetir simulación
                  </button>
                )}
              </div>
            </div>
            <p className="coming-soon__caption">
              Simulación de una conversación de WhatsApp. El canal de WhatsApp
              todavía no está disponible.
            </p>
          </div>
        </section>

        <section className="control-section">
          <div className="section-intro section-intro--center" data-reveal>
            <span className="section-index">05 · TODO BAJO CONTROL</span>
            <h2>Muchas conversaciones. Una sola operativa.</h2>
          </div>
          <div className="control-grid">
            <article className="control-card control-card--app" data-reveal>
              <div>
                <span>Agenda centralizada</span>
                <h3>Todo termina en el mismo sitio.</h3>
                <p>
                  Llamadas, mensajes, confirmaciones y cancelaciones quedan
                  organizadas para el equipo.
                </p>
              </div>
              <img
                src={appImage}
                alt="Aplicación de gestión TuMesaYa"
                loading="lazy"
              />
            </article>
            <article className="control-card control-card--stat" data-reveal>
              <span>Menos tareas manuales</span>
              <strong>Atiende · confirma · recuerda · cancela</strong>
              <div className="stat-loop" aria-hidden="true">
                <i>TEL</i>
                <span>→</span>
                <i className="stat-loop__wa is-soon">WA</i>
                <span>→</span>
                <b>AGENDA</b>
              </div>
              <p className="stat-loop__note">
                <span className="tab-soon">Próximamente</span>
                WhatsApp aún no está disponible.
              </p>
            </article>
            <article className="control-card control-card--setup" data-reveal>
              <span>En tres pasos</span>
              <ol>
                <li>
                  <b>01</b> Configuramos horarios y mesas
                </li>
                <li>
                  <b>02</b> Adaptamos el asistente
                </li>
                <li>
                  <b>03</b> Empieza a gestionar reservas
                </li>
              </ol>
            </article>
          </div>
        </section>

        <section className="testimonial testimonial--v2" id="resultados">
          <img
            src={testimonialImage}
            alt="Mónica González, Gastro Bar Upstairs"
            loading="lazy"
          />
          <figure data-reveal>
            <span className="section-index">LA EXPERIENCIA EN SALA CAMBIA</span>
            <blockquote>
              “Antes perdíamos muchas llamadas en horas punta. Ahora cada
              solicitud se atiende y el equipo puede centrarse en el servicio.”
            </blockquote>
            <figcaption>
              <strong>Mónica González</strong>
              <span>Gastro Bar “Upstairs”</span>
            </figcaption>
          </figure>
        </section>

        <section className="demo-section demo-section--v2" id="demo">
          <div className="demo-section__copy" data-reveal>
            <span className="section-index section-index--yellow">
              SOLICITA UNA DEMO
            </span>
            <h2>
              ¿Qué reservas estás perdiendo mientras nadie puede responder?
            </h2>
            <p>
              Te enseñamos cómo funcionaría TuMesaYa con los horarios, mesas y
              operativa de tu restaurante.
            </p>
            <ul className="demo-checks">
              <li>
                El canal de teléfono en directo. WhatsApp
                <span className="tab-soon">Próximamente</span>
              </li>
              <li>Sin compromiso</li>
              <li>Adaptada a tu restaurante</li>
            </ul>
          </div>
          <div className="form-card form-card--v2" data-reveal>
            {submitState === 'success' ? (
              <div className="form-message form-message--success" role="status">
                <span>✓</span>
                <h3>Solicitud recibida</h3>
                <p>
                  Gracias. Nuestro equipo se pondrá en contacto contigo para
                  preparar la demo.
                </p>
              </div>
            ) : (
              <>
                <div className="form-card__heading">
                  <span>Cuéntanos sobre tu restaurante</span>
                  <strong>Preparamos una demo para tu caso.</strong>
                </div>
                <TuMesaYaForm
                  onSubmit={handleFormSubmit}
                  isSubmitting={isSubmitting}
                  variant="demo"
                  compact
                  submitLabel="Solicitar una demo"
                />
                {submitState === 'error' && (
                  <p className="form-error" role="alert">
                    No hemos podido enviar la solicitud. Inténtalo de nuevo o
                    llámanos al 911 09 53 89.
                  </p>
                )}
              </>
            )}
          </div>
        </section>
      </main>

      <footer className="site-footer site-footer--v2">
        <div className="site-footer__brand">
          <img src={logo} alt="TuMesaYa" />
          <p>Teléfono 24/7. WhatsApp, próximamente.</p>
        </div>
        <div className="site-footer__links">
          <a href="#canales">
            Teléfono + WhatsApp
            <span className="tab-soon tab-soon--ink">Próximamente</span>
          </a>
          <a href="#servicio">Durante el servicio</a>
          <a href="#no-shows">Menos no-shows</a>
          <a href="/prueba">Prueba gratuita de 30 min</a>
          <a href={LOGIN_URL}>Acceder</a>
          <a href={REGISTER_URL}>Crear cuenta</a>
        </div>
        <div className="site-footer__legal">
          <a href="/aviso-legal">Aviso legal</a>
          <a href="/terminos">Términos y condiciones</a>
          <a href="/politica-privacidad">Política de privacidad</a>
          <a href="/cookies">Política de cookies</a>
          <button type="button" data-cc="show-preferencesModal">
            Configurar cookies
          </button>
        </div>
        <div className="site-footer__contact">
          <a href="tel:+34911095389">911 09 53 89</a>
          <div className="social-links" aria-label="Redes sociales">
            <a href="https://www.facebook.com/tumesaya" aria-label="Facebook">
              <img src={facebookIcon} alt="" />
            </a>
            <a
              href="https://www.linkedin.com/company/tumesaya"
              aria-label="LinkedIn"
            >
              <img src={linkedinIcon} alt="" />
            </a>
          </div>
        </div>
        <p className="site-footer__copyright">
          © {new Date().getFullYear()} TuMesaYa
        </p>
      </footer>

      <a
        className={`mobile-sticky-cta ${hideStickyCta ? 'is-hidden' : ''}`}
        href={REGISTER_URL}
        onClick={() => handleDemoClick('mobile-sticky-alta')}
      >
        Crear mi cuenta
        <span aria-hidden="true">→</span>
      </a>
    </div>
  );
};

export default LandingPage;
